import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  IconButton,
  Divider,
  CircularProgress,
  Drawer,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BugReportIcon from "@mui/icons-material/BugReport";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { Image } from "@mui/icons-material";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import ViewModuleIcon from "@mui/icons-material/ViewModule";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PublicIcon from "@mui/icons-material/Public";
import DevicesIcon from "@mui/icons-material/Devices";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import apiService from "../../service/Api";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBugAssignments,
  fetchBugDashboard,
} from "../../store/slice/bug-fix/bugSlice";
import { BugReport } from "../../store/slice/bug-fix/types";
import { AppDispatch, RootState } from "../../store/Index";

/* =====================
   Types
===================== */

interface StatCard {
  label: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  subTitleColor: string;
}

interface BugAssiment {
  bug_id: number | string;
  bug: BugReport;
  assigned_to_id?: number;
  // assigned_to?:string;
  priority: "low" | "medium" | "high" | "urgent";
}

const bugtype_choices = {
  ui_issue: "Something doesn’t look right",
  feature_not_working: "A feature isn’t working",
  performance: "The system feels slow",
  access_issue: "I can’t access something",
  other: "Something else",
};

const occurance_choices = {
  every_time: "Every time",
  sometimes: "Sometimes",
  once: "Just once",
};

const impact_choices = {
  minor: "Minor inconvenience",
  slowed: "Slowed me down",
  blocked: "Blocked my work",
};

const status_choices = {
  new: "New",
  assigned: "Assigned",
  in_progress: "In Progress",
  resolved: "Resolved",
  blocked: "Blocked",
};

const isOverdue = (bug: BugReport) =>
  Boolean(
    bug.due_date &&
    bug.status !== "resolved" &&
    new Date(bug.due_date) < new Date(),
  );

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(date);

  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";

  return `${get("day")} ${get("month")} ${get("year")} ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
};

export default function BugsDevDashboard() {
  const [selectedBug, setSelectedBug] = React.useState<BugReport | null>(null);
  const [status, setStatus] = React.useState("new");
  const labelColor = "rgba(148, 163, 184, 1)";
  const dispatch = useDispatch<AppDispatch>();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [priorityFilter, setPriorityFilter] = React.useState("all");
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [assigningBugId, setAssigningBugId] = React.useState<string | null>(
    null,
  );
  const [statusUpdating, setStatusUpdating] = React.useState(false);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [isImageOpen, setIsImageOpen] = React.useState(false);

  const { assignments, dashboard } = useSelector(
    (state: RootState) => state.bugs,
  );

  const user = useSelector((state: RootState) => state.auth.user);

  const bugAssignments = React.useMemo(() => {
    if (!assignments) return [];

    return assignments;
  }, [assignments]);

  const filteredAssignments = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return bugAssignments.filter((assignment) => {
      if (statusFilter !== "all" && assignment.bug.status !== statusFilter) {
        return false;
      }
      if (priorityFilter !== "all" && assignment.priority !== priorityFilter) {
        return false;
      }
      if (!query) return true;

      const haystack = [
        assignment.bug.description,
        assignment.bug.problem,
        assignment.bug.id,
        assignment.bug.module_name,
        bugtype_choices[assignment.bug.bugtype],
        assignment.bug.status,
        assignment.priority,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [bugAssignments, searchQuery, statusFilter, priorityFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredAssignments.length / rowsPerPage),
  );
  const currentPage = Math.min(page, totalPages);
  const pagedAssignments = React.useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredAssignments.slice(start, start + rowsPerPage);
  }, [filteredAssignments, currentPage, rowsPerPage]);

  React.useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, priorityFilter, rowsPerPage]);

  React.useEffect(() => {
    if (page !== currentPage) {
      setPage(currentPage);
    }
  }, [page, currentPage]);

  const recentBugs = React.useMemo(() => {
    if (!dashboard?.recent_bugs) return [];

    return dashboard.recent_bugs;
  }, [dashboard]);

  const stats: StatCard[] = React.useMemo(() => {
    if (!dashboard) {
      return [
        {
          label: "Open bugs",
          value: 0,
          subtitle: "+0 Since yesterday",
          icon: (
            <BugReportIcon sx={{ color: "#007AFF", height: 24, width: 24 }} />
          ),
          color: "rgba(0, 122, 255, 0.15)",
          subTitleColor: "#8F8F8F",
        },
        {
          label: "Critical",
          value: 0,
          subtitle: "Needs immediate attention",
          icon: (
            <WhatshotIcon sx={{ color: "#FF3B30", height: 24, width: 24 }} />
          ),
          color: "rgba(255, 59, 48,0.15)",
          subTitleColor: "#FF3B30",
        },
        {
          label: "Avg Fix time",
          value: "0d",
          subtitle: "—",
          icon: (
            <AccessTimeIcon
              sx={{ color: "rgba(175, 82, 222, 1)", height: 24, width: 24 }}
            />
          ),
          color: "rgba(175, 82, 222, 0.15)",
          subTitleColor: "#0D9488",
        },
        {
          label: "Resolved",
          value: 0,
          subtitle: "Last 7 days",
          icon: (
            <CheckCircleIcon
              sx={{ color: "rgba(13, 148, 136,1)", height: 24, width: 24 }}
            />
          ),
          color: "rgba(13, 148, 136,0.15)",
          subTitleColor: "#8F8F8F",
        },
      ];
    }

    return [
      {
        label: "Open bugs",
        value: dashboard.stats.open_bugs,
        subtitle:
          dashboard.stats.new_open_bugs_last_24h > 0
            ? `+${dashboard.stats.new_open_bugs_last_24h} Since yesterday`
            : "No new bugs since yesterday",
        icon: (
          <BugReportIcon sx={{ color: "#007AFF", height: 24, width: 24 }} />
        ),
        color: "rgba(0, 122, 255, 0.15)",
        subTitleColor: "#8F8F8F",
      },
      {
        label: "Critical",
        value: dashboard.stats.critical_bugs,
        subtitle: "Needs immediate attention",
        icon: <WhatshotIcon sx={{ color: "#FF3B30", height: 24, width: 24 }} />,
        color: "rgba(255, 59, 48,0.15)",
        subTitleColor: "#FF3B30",
      },
      {
        label: "Avg Fix time",
        value: `${dashboard.stats.avg_fix_time_days}d`,
        subtitle:
          dashboard.stats.avg_fix_trend === "improving"
            ? "↓ Improving"
            : dashboard.stats.avg_fix_trend === "worsening"
              ? "↑ Worsening"
              : "— Stable",
        icon: (
          <AccessTimeIcon
            sx={{ color: "rgba(175, 82, 222, 1)", height: 24, width: 24 }}
          />
        ),
        color: "rgba(175, 82, 222, 0.15)",
        subTitleColor:
          dashboard.stats.avg_fix_trend === "improving"
            ? "#0D9488"
            : dashboard.stats.avg_fix_trend === "worsening"
              ? "#FF3B30"
              : "#8F8F8F",
      },
      {
        label: "Resolved",
        value: dashboard.stats.resolved_last_7_days,
        subtitle: "Last 7 days",
        icon: (
          <CheckCircleIcon
            sx={{ color: "rgba(13, 148, 136,1)", height: 24, width: 24 }}
          />
        ),
        color: "rgba(13, 148, 136,0.15)",
        subTitleColor: "#8F8F8F",
      },
    ];
  }, [dashboard]);

  const openBugsCount = Number(dashboard?.stats?.open_bugs ?? 0);
  const workloadLabel =
    openBugsCount > 5 ? "Heavy" : openBugsCount >= 3 ? "Moderate" : "Low";
  const workloadColors =
    workloadLabel === "Heavy"
      ? { bg: "rgba(255, 59, 48, 0.15)", fg: "rgba(255, 59, 48, 1)" }
      : workloadLabel === "Moderate"
        ? { bg: "rgba(255, 149, 0, 0.15)", fg: "rgba(255, 149, 0, 1)" }
        : { bg: "rgba(52, 199, 89, 0.15)", fg: "rgba(52, 199, 89, 1)" };

  const handleAssign = async (bug1: BugReport) => {
    try {
      setAssigningBugId(bug1.id);
      const prio =
        bug1.impact === "minor"
          ? "low"
          : bug1.impact === "slowed"
            ? "medium"
            : bug1.impact === "blocked" && bug1.occurence === "every_time"
              ? "high"
              : "urgent";
      const id = bug1.id.split("-");

      const bugAssign: BugAssiment = {
        bug_id: Number(id[id.length - 1]),
        assigned_to_id: user?.id,
        bug: bug1,
        priority: prio,
      };

      await apiService.post<BugAssiment>(
        "/api/bug-report/bug-assignments/",
        bugAssign,
      );
      // dispatch(fetchBugs())

      dispatch(fetchBugDashboard());
      if (user?.id) {
        dispatch(fetchBugAssignments(user.id));
      }
    } catch (error: unknown) {
      console.log(error);
    } finally {
      setAssigningBugId(null);
    }
  };

  const handleUpdateStatus = async (status: string, bugId: string) => {
    try {
      setStatusUpdating(true);

      const id = bugId.split("-");

      const response = await apiService.patch<BugReport>(
        `/api/bug-report/bugs/${Number(id[id.length - 1])}/`,
        { status },
      );
      setStatus(response.data.status);
      dispatch(fetchBugDashboard());
      if (user?.id) {
        dispatch(fetchBugAssignments(user.id));
      }
    } catch (error: unknown) {
      console.log(error);
    } finally {
      setStatusUpdating(false);
    }
  };

  React.useEffect(() => {
    // dispatch(fetchBugs());
    const load = async () => {
      if (user?.id) {
        await dispatch(fetchBugAssignments(user.id));
      }
      await dispatch(fetchBugDashboard());
      setInitialLoading(false);
    };
    load();
  }, [dispatch, user?.id]);

  React.useEffect(() => {
    if (selectedBug) {
      setStatus(selectedBug.status);
    }
  }, [selectedBug]);

  const InfoItem = ({
    icon,
    label,
    value,
  }: {
    icon: React.ReactNode;
    label: string;
    value?: string;
  }) => (
    <Stack direction="row" spacing={1.5} flex={1} alignItems="flex-start">
      {icon}
      <Box>
        <Typography color={labelColor} variant="caption">
          {label}
        </Typography>
        <Typography variant="body2">{value || "-"}</Typography>
      </Box>
    </Stack>
  );
  if (initialLoading) {
    return (
      <Box
        p={3}
        bgcolor="#f6f8fc"
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
        {stats.map((stat) => (
          <Card
            key={stat.label}
            sx={{
              flex: 1,
              borderRadius: "16px",
              boxShadow: "none",
              border: "none",
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    bgcolor: stat.color,
                    p: 1.5,
                    borderRadius: "100%",
                    height: 50,
                    width: 50,
                  }}
                >
                  {stat.icon}
                </Box>
                <Box>
                  <Typography variant="h6">{stat.value}</Typography>
                  <Typography variant="body2">{stat.label}</Typography>
                  <Typography variant="caption" color={stat.subTitleColor}>
                    {stat.subtitle}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* =====================
                     RECENT + WORKLOAD
                  ===================== */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
        <Card
          sx={{
            flex: 3,
            borderRadius: "16px",
            boxShadow: "none",
            border: "none",
          }}
        >
          <CardContent>
            <Typography fontWeight={600} mb={2}>
              Recent Bug Reports
            </Typography>

            <Stack spacing={1.5} sx={{ maxHeight: 300, overflowY: "auto" }}>
              {recentBugs.length === 0 ? (
                <Box
                  p={3}
                  borderRadius="12px"
                  bgcolor="#f3f6f9"
                  textAlign="center"
                  sx={{ flex: 1, height: 250 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No recent bugs.
                  </Typography>
                </Box>
              ) : (
                recentBugs.map((bug: BugReport, index) => {
                  const impactColor =
                    bug.impact === "blocked"
                      ? "#f44336"
                      : bug.impact === "slowed"
                        ? "#ff9500"
                        : "rgba(148, 163, 184, 1)";

                  return (
                    <Box
                      key={index}
                      onClick={() => setSelectedBug(bug)}
                      sx={{
                        borderRadius: "12px",
                        border: "1px solid rgba(226, 232, 240, 1)",
                        borderLeft: `3px solid ${impactColor}`,
                        bgcolor: "#fff",
                        p: 2,
                        cursor: "pointer",
                        transition: "border-color 0.15s ease",
                        "&:hover": { borderColor: "rgba(160, 174, 192, 1)" },
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={2}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {bug.id}
                          </Typography>
                          <Typography fontWeight={600} noWrap>
                            {bug.description}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ display: "block" }}
                          >
                            {bug.problem}
                          </Typography>
                        </Box>
                        {(bug.impact === "blocked" ||
                          bug.impact === "slowed") && (
                          <WarningIcon
                            sx={{
                              color: impactColor,
                              height: 20,
                              width: 20,
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        rowGap={1}
                        mt={1.5}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          flexWrap="wrap"
                          rowGap={1}
                          divider={
                            <Box
                              sx={{
                                width: "1px",
                                height: "14px",
                                bgcolor: "rgba(226, 232, 240, 1)",
                              }}
                            />
                          }
                        >
                          <Chip
                            label={bug.module_name}
                            size="small"
                            sx={{
                              color: "#fff",
                              backgroundColor: "#A2845E",
                              height: 22,
                            }}
                          />
                          <Chip
                            label={status_choices[bug.status]}
                            size="small"
                            sx={{
                              color: "rgba(255, 149, 0, 1)",
                              backgroundColor: "rgba(255, 149, 0, 0.05)",
                              border: "1px solid rgba(255, 149, 0, 1)",
                              height: 22,
                            }}
                          />
                          <Typography variant="caption" color="error">
                            {formatDateTime(bug.created_at)}
                          </Typography>
                        </Stack>

                        <Button
                          variant="contained"
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleAssign(bug);
                          }}
                          disabled={assigningBugId === bug.id}
                          sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            flexShrink: 0,
                          }}
                        >
                          {assigningBugId === bug.id ? (
                            <CircularProgress
                              size={16}
                              sx={{ color: "#fff" }}
                            />
                          ) : (
                            "Take work"
                          )}
                        </Button>
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={{
            flex: 1,
            borderRadius: "12px",
            boxShadow: "none",
            border: "none",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "26px",
                width: "100%",
              }}
            >
              <Typography fontWeight={600}>My Workload</Typography>

              <Typography
                sx={{
                  backgroundColor: workloadColors.bg,
                  color: workloadColors.fg,
                  textAlign: "center",
                  px: 1,
                  py: 0.25,
                  fontSize: "10px",
                  borderRadius: "15px",
                  fontWeight: 600,
                }}
              >
                {workloadLabel}
              </Typography>
            </Box>

            <Stack alignItems="center" spacing={5}>
              <Box position="relative">
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                  }}
                >
                  {/* Background Track */}
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={100}
                    thickness={3}
                    sx={{ color: "#f44336" }}
                  />
                  {/* Foreground Progress */}
                  <CircularProgress
                    variant="determinate"
                    value={
                      dashboard?.my_workload?.total_assigned
                        ? (dashboard.my_workload.total_resolved /
                            dashboard.my_workload.total_assigned) *
                          100
                        : 0
                    }
                    size={100}
                    thickness={3}
                    sx={{
                      color: "#f0f4f8",
                      position: "absolute",
                      left: 0,
                    }}
                  />
                </Box>
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  sx={{
                    transform: "translate(-50%, -50%)",
                    alignItems: "center",
                  }}
                >
                  <Typography fontWeight={600}>{stats[0].value}</Typography>
                  <Typography variant="caption">Bugs</Typography>
                </Box>
              </Box>

              <Stack direction="row" spacing={3}>
                <Box
                  textAlign="center"
                  sx={{
                    backgroundColor: "background.paper",
                    borderRadius: "16px",
                    p: 1,
                  }}
                >
                  <Typography fontWeight={600}>
                    {dashboard?.my_workload.total_assigned}
                  </Typography>
                  <Typography variant="caption">Total assigned</Typography>
                </Box>
                <Box
                  textAlign="center"
                  sx={{
                    backgroundColor: "background.paper",
                    borderRadius: "16px",
                    p: 1,
                  }}
                >
                  <Typography fontWeight={600}>
                    {dashboard?.my_workload.high_priority}
                  </Typography>
                  <Typography variant="caption">High Priority</Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* =====================
         BUG LIST
      ===================== */}
      <Card sx={{ boxShadow: "none", border: "none", borderRadius: "16px" }}>
        <CardContent>
          <Typography fontWeight={600} mb={2}>
            Bug List
          </Typography>

          <Stack direction="row" spacing={2} mb={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search bugs..."
              fullWidth
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "10px",
                },
              }}
            />
            <Select
              size="small"
              value={statusFilter}
              onChange={(event) => setStatusFilter(String(event.target.value))}
              sx={{ minWidth: 140, borderRadius: "10px" }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="blocked">Blocked</MenuItem>
            </Select>
            <Select
              size="small"
              value={priorityFilter}
              onChange={(event) =>
                setPriorityFilter(String(event.target.value))
              }
              sx={{ minWidth: 140, borderRadius: "10px" }}
            >
              <MenuItem value="all">All Priority</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          </Stack>

          <Divider />
          <Box
            sx={{
              maxHeight: 500,
              overflowY: "auto",
            }}
          >
            <Stack spacing={2} mt={2}>
              {pagedAssignments.length === 0 ? (
                <Box
                  p={3}
                  borderRadius="16px"
                  bgcolor="#f3f6f9"
                  textAlign="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    No bug assignments found.
                  </Typography>
                </Box>
              ) : (
                pagedAssignments.map((bug, index) => {
                  const priorityColor =
                    {
                      urgent: "#f44336",
                      high: "#ff9500",
                      medium: "#378ADD",
                      low: "rgba(148, 163, 184, 1)",
                    }[bug.priority] ?? "rgba(148, 163, 184, 1)";
                  const overdue = isOverdue(bug.bug);

                  return (
                    <Box
                      key={index}
                      onClick={() => setSelectedBug(bug.bug)}
                      sx={{
                        borderRadius: "16px",
                        border: "1px solid rgba(226, 232, 240, 1)",
                        borderLeft: `3px solid ${priorityColor}`,
                        bgcolor: "#fff",
                        p: 2,
                        cursor: "pointer",
                        transition: "border-color 0.15s ease",
                        "&:hover": { borderColor: "rgba(160, 174, 192, 1)" },
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={2}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {bug.bug.id}
                          </Typography>
                          <Typography fontWeight={600} noWrap>
                            {bug.bug.description}
                          </Typography>
                        </Box>
                        {(bug.bug.impact === "blocked" ||
                          bug.bug.impact === "slowed") && (
                          <WarningAmberIcon
                            sx={{
                              color:
                                bug.bug.impact === "blocked"
                                  ? "#f44336"
                                  : "#ff9500",
                              height: 20,
                              width: 20,
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        flexWrap="wrap"
                        rowGap={1}
                        mt={1.5}
                        divider={
                          <Box
                            sx={{
                              width: "1px",
                              height: "14px",
                              bgcolor: "rgba(226, 232, 240, 1)",
                            }}
                          />
                        }
                      >
                        <Chip
                          label={bug.bug.module_name}
                          size="small"
                          sx={{
                            color: "#fff",
                            backgroundColor: "#A2845E",
                            height: 22,
                            borderRadius: "16px",
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {bugtype_choices[bug.bug.bugtype]}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          <AccessTimeIcon
                            sx={{
                              height: 14,
                              width: 14,
                              color: overdue
                                ? "#f44336"
                                : "rgba(148, 163, 184, 1)",
                            }}
                          />
                          <Typography
                            variant="caption"
                            color={overdue ? "#f44336" : "text.secondary"}
                            fontWeight={overdue ? 600 : 400}
                          >
                            {bug.bug.due_date
                              ? formatDateTime(bug.bug.due_date)
                              : "No due date"}
                          </Typography>
                        </Stack>

                        <Chip
                          label={status_choices[bug.bug.status]}
                          size="small"
                          sx={{
                            color: "rgba(255, 149, 0, 1)",
                            backgroundColor: "rgba(255, 149, 0, 0.05)",
                            border: "1px solid rgba(255, 149, 0, 1)",
                            height: 22,
                            borderRadius: "16px",
                          }}
                        />

                        {bug.bug.image && (
                          <PhotoLibraryIcon
                            sx={{
                              height: 18,
                              width: 18,
                              color: "rgba(165, 165, 165, 1)",
                            }}
                          />
                        )}
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Stack>
          </Box>

          <Box
            mt={2}
            display="flex"
            justifyContent="flex-end"
            alignItems="flex-end"
            sx={{}}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mr: 5 }}
            >
              <Select
                size="small"
                value={rowsPerPage}
                onChange={(event) => setRowsPerPage(Number(event.target.value))}
                sx={{ minWidth: 120, borderRadius: "10px" }}
              >
                <MenuItem value={10}>10 / page</MenuItem>
                <MenuItem value={25}>25 / page</MenuItem>
                <MenuItem value={50}>50 / page</MenuItem>
              </Select>
            </Stack>

            <Box
              mt={2}
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={3}
            >
              <IconButton
                size="small"
                disabled={currentPage === 1}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                sx={{
                  border: "1px solid rgba(226, 232, 240, 1)",
                  borderRadius: "16px",
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>

              <Box
                sx={{
                  backgroundColor: "#724E41",
                  px: 2,
                  py: 0.5,
                  borderRadius: "16px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 32,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                  }}
                >
                  {currentPage}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                of
              </Typography>

              <Typography>{totalPages}</Typography>

              <IconButton
                size="small"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setPage((prev) => Math.min(prev + 1, totalPages))
                }
                sx={{
                  border: "1px solid rgba(226, 232, 240, 1)",
                  borderRadius: "16px",
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* =====================
         RIGHT DRAWER
      ===================== */}
      <Drawer
        anchor="right"
        open={Boolean(selectedBug)}
        onClose={() => setSelectedBug(null)}
        PaperProps={{ sx: { width: 420, p: 2 } }}
      >
        {selectedBug && (
          <>
            <Stack direction="row" justifyContent="space-between">
              <Stack spacing={2} direction={"row"}>
                <Typography
                  variant="caption"
                  color="rgba(130, 130, 130, 1)"
                  sx={{ fontSize: "14px" }}
                >
                  {selectedBug.id}
                </Typography>

                <Stack direction={"row"} spacing={0.5}>
                  <WarningIcon
                    sx={{
                      height: 20,
                      width: 20,
                      color: "rgba(255, 59, 48, 1)",
                    }}
                  />
                  <Typography sx={{ color: "rgba(255, 59, 48, 1)" }}>
                    {impact_choices[selectedBug.impact]}
                  </Typography>
                </Stack>
              </Stack>
              <IconButton onClick={() => setSelectedBug(null)}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Typography fontWeight={600} mt={2}>
              {selectedBug.description}
            </Typography>

            <Stack mt={2} spacing={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Update status
              </Typography>

              <Select
                size="small"
                value={status}
                onChange={(e) =>
                  handleUpdateStatus(e.target.value, selectedBug.id)
                }
                sx={{ minWidth: 160 }}
                disabled={statusUpdating}
              >
                <MenuItem value="assigned">
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      borderRadius: "16px",
                      px: 1.5,
                      py: 0.5,
                      border: "2px solid #378ADD",
                    }}
                  >
                    <Box
                      sx={{
                        height: 6,
                        width: 6,
                        borderRadius: "50%",
                        backgroundColor: "#378ADD",
                      }}
                    />
                    <Typography color="#378ADD">Assigned</Typography>
                  </Stack>
                </MenuItem>
                {/* NEW */}
                <MenuItem value="new">
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      borderRadius: "16px",
                      px: 1.5,
                      py: 0.5,
                      border: "2px solid rgba(13, 148, 136, 1)",
                    }}
                  >
                    <Box
                      sx={{
                        height: 6,
                        width: 6,
                        borderRadius: "50%",
                        backgroundColor: "rgba(13, 148, 136, 1)",
                      }}
                    />
                    <Typography color="rgba(13, 148, 136, 1)">New</Typography>
                  </Stack>
                </MenuItem>

                {/* IN PROGRESS */}
                <MenuItem value="in_progress">
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      borderRadius: "16px",
                      px: 1.5,
                      py: 0.5,
                      border: "2px solid rgba(13, 148, 136, 1)",
                    }}
                  >
                    <Box
                      sx={{
                        height: 6,
                        width: 6,
                        borderRadius: "50%",
                        backgroundColor: "rgba(13, 148, 136, 1)",
                      }}
                    />
                    <Typography color="rgba(13, 148, 136, 1)">
                      In Progress
                    </Typography>
                  </Stack>
                </MenuItem>

                {/* COMPLETED */}
                <MenuItem value="resolved">
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      borderRadius: "16px",
                      px: 1.5,
                      py: 0.5,
                      border: "2px solid rgba(13, 148, 136, 1)",
                    }}
                  >
                    <Box
                      sx={{
                        height: 6,
                        width: 6,
                        borderRadius: "50%",
                        backgroundColor: "rgba(13, 148, 136, 1)",
                      }}
                    />
                    <Typography color="rgba(13, 148, 136, 1)">
                      Resolved
                    </Typography>
                  </Stack>
                </MenuItem>
                <MenuItem value="blocked">
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      borderRadius: "16px",
                      px: 1.5,
                      py: 0.5,
                      border: "2px solid #f44336",
                    }}
                  >
                    <Box
                      sx={{
                        height: 6,
                        width: 6,
                        borderRadius: "50%",
                        backgroundColor: "#f44336",
                      }}
                    />
                    <Typography color="#f44336">Blocked</Typography>
                  </Stack>
                </MenuItem>
              </Select>
            </Stack>

            <Divider />

            <Stack
              sx={{
                backgroundColor: "rgba(204, 230, 255, 1)",
                flex: 1,
                p: 1.5,
                my: 2,

                borderTop: "2px solid rgba(95, 177, 255, 1)",
                borderBottom: "3px solid rgba(95, 177, 255, 1)",
              }}
              spacing={1}
            >
              <Typography sx={{ fontWeight: "600px", fontSize: "14px" }}>
                SMART SUMMARY
              </Typography>
              <Typography>{selectedBug.description}</Typography>
            </Stack>

            <Typography>AUTO CAPTURED CONTEXT</Typography>

            <Stack spacing={3} mt={1}>
              {/* Row 1 */}
              <Stack direction="row" spacing={4}>
                <InfoItem
                  icon={<PhotoLibraryIcon sx={{ color: labelColor }} />}
                  label="Page"
                  value={selectedBug.page}
                />
                <InfoItem
                  icon={<ViewModuleIcon sx={{ color: labelColor }} />}
                  label="Module"
                  value={selectedBug.module_name}
                />
              </Stack>

              {/* Row 2 */}
              <Stack direction="row" spacing={4}>
                <InfoItem
                  icon={<PersonOutlineIcon sx={{ color: labelColor }} />}
                  label="Reporter Role"
                  value={selectedBug.reporter_role}
                />
                <InfoItem
                  icon={<PublicIcon sx={{ color: labelColor }} />}
                  label="Browser"
                  value={selectedBug.browser}
                />
              </Stack>

              {/* Row 3 */}
              <Stack direction="row" spacing={4}>
                <InfoItem
                  icon={<DevicesIcon sx={{ color: labelColor }} />}
                  label="Device"
                  value={selectedBug.device}
                />
                <InfoItem
                  icon={<AspectRatioIcon sx={{ color: labelColor }} />}
                  label="Screen Resolution"
                  value={selectedBug.screen}
                />
              </Stack>

              {/* Row 4 */}
              <Stack direction="row" spacing={4}>
                <InfoItem
                  icon={<InfoOutlinedIcon sx={{ color: labelColor }} />}
                  label="App Version"
                  value={selectedBug.version}
                />
                {/* Empty column keeps alignment */}
                <Box flex={1} />
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography>USER REPORT</Typography>
            <Stack spacing={2} mt={1}>
              <Stack spacing={1}>
                <Typography color="rgba(148, 163, 184, 1)">
                  Issue Type:
                </Typography>
                <Typography>{bugtype_choices[selectedBug.bugtype]}</Typography>
              </Stack>

              <Stack spacing={1}>
                <Typography color="rgba(148, 163, 184, 1)">
                  What they were trying to do:
                </Typography>
                <Typography>{selectedBug.description}</Typography>
              </Stack>

              <Stack spacing={1}>
                <Typography color="rgba(148, 163, 184, 1)">
                  What went wrong:
                </Typography>
                <Typography>{selectedBug.problem}</Typography>
              </Stack>

              <Stack direction={"row"} spacing={5}>
                <Stack spacing={0.5}>
                  <Typography color="rgba(148, 163, 184, 1)">
                    Reproducibility:
                  </Typography>
                  <Typography>
                    {occurance_choices[selectedBug.occurence]}
                  </Typography>
                </Stack>
                <Stack spacing={0.5}>
                  <Typography color="rgba(148, 163, 184, 1)">
                    Impact:
                  </Typography>
                  <Typography>{impact_choices[selectedBug.impact]}</Typography>
                </Stack>
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography>VISUAL EVIDENCE</Typography>

            <Button
              variant="outlined"
              size="medium"
              disabled={!selectedBug.image}
              onClick={() => setIsImageOpen(true)}
              sx={{
                backgroundColor: "rgba(240, 244, 248, 1)",
                border: "1px rgba(227, 227, 227, 1) ",
                borderRadius: "26px",
                gap: 1.5,
                color: "#434546",
                py: 2,
                alignSelf: "flex-start",
                px: 4,
                mt: 2,
              }}
            >
              <Image
                sx={{
                  height: 20,
                  width: 20,
                }}
              />

              <Typography>View Screenshot</Typography>
            </Button>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1}>
              <Typography>TIMELINE</Typography>

              <Typography color="rgba(148, 163, 184, 1)">
                Reported {formatDateTime(selectedBug.created_at)}
              </Typography>
            </Stack>

            <Drawer
              anchor="bottom"
              open={isImageOpen}
              onClose={() => setIsImageOpen(false)}
              PaperProps={{
                sx: { p: 2, borderTopLeftRadius: 16, borderTopRightRadius: 16 },
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography fontWeight={600}>Screenshot</Typography>
                  <IconButton onClick={() => setIsImageOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {selectedBug.image ? (
                    <Box
                      component="img"
                      src={
                        typeof selectedBug.image === "string"
                          ? selectedBug.image
                          : URL.createObjectURL(selectedBug.image)
                      }
                      alt="Bug screenshot"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: "70vh",
                        borderRadius: 2,
                      }}
                    />
                  ) : (
                    <Typography color="text.secondary">
                      No screenshot available.
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Drawer>
          </>
        )}
      </Drawer>
    </Box>
  );
}
